import { useEffect, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent, ReactNode } from "react";
import { GripVertical } from "lucide-react";
import { useIntl } from "react-intl";
import { buttonClasses } from "@/components/design/button-classes";

// A pointer that moves this far while pressed drags; less is a tap.
const DRAG_THRESHOLD_PX = 5;
const MOVED = { id: "common.sortable.moved", defaultMessage: "{item}: position {position} of {total}" };

// A list the reader puts in order. Every way runs through the grip, a real
// button in front of each row:
//  - drag it with mouse, pen or finger (Pointer Events, so touch works where
//    native HTML drag and drop does not); the rows reorder live and the
//    dragged row carries the wash (`bg-ink/10`);
//  - tap it to pick the row up (same wash, aria-pressed on the grip), then tap
//    anywhere on another row to put it in that row's place, for anyone who
//    cannot drag (WCAG 2.5.7). A tap on the own grip or Escape puts it back;
//  - with the keyboard (WCAG 2.1.1): Enter or Space picks up, the up and down
//    arrows move the row one place at a time, Enter or Space drops it there,
//    Escape restores the order it had. Focus stays on the grip of the moved row.
// Every step is announced to screen readers. The list holds no order of its
// own: `onMove(from, to)` reports the move and the caller passes the reordered
// `items` back.
export function SortableList<T>({
  items,
  getKey,
  getLabel,
  renderItem,
  onMove,
  onPickedChange,
}: {
  items: readonly T[];
  getKey: (item: T) => string;
  // The entry's name in button labels and announcements.
  getLabel: (item: T) => string;
  renderItem: (item: T, index: number) => ReactNode;
  onMove: (from: number, to: number) => void;
  // The row picked up to be placed by a tap, or null; e.g. to word a hint.
  onPickedChange?: (item: T | null) => void;
}) {
  const intl = useIntl();
  const rows = useRef(new Map<string, HTMLLIElement>());
  const [drag, setDrag] = useState<{ key: string; from: number; to: number } | null>(null);
  // `to` is where the row shows while the keyboard moves it; it lands there on drop.
  const [picked, setPicked] = useState<{ key: string; to: number } | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const press = useRef<{ key: string; index: number; y: number } | null>(null);
  // The click a browser sends after a drag ends on the grip is not a tap.
  const swallowClick = useRef(false);

  const pickedFrom = picked ? items.findIndex((item) => getKey(item) === picked.key) : -1;
  const active = picked && pickedFrom >= 0 ? { ...picked, from: pickedFrom, to: Math.min(picked.to, items.length - 1) } : null;
  const shown = drag ? reordered(items, drag.from, drag.to) : active ? reordered(items, active.from, active.to) : items;

  const say = (message: { id: string; defaultMessage: string }, item: T, position: number) =>
    setAnnouncement(intl.formatMessage(message, { item: getLabel(item), position: position + 1, total: items.length }));

  function pickUp(index: number) {
    const item = items[index];
    if (item === undefined) return;
    setPicked({ key: getKey(item), to: index });
    onPickedChange?.(item);
    say(
      {
        id: "common.sortable.picked",
        defaultMessage:
          "{item} picked up at position {position} of {total}. Move it with the arrow keys, drop it with Enter, cancel with Escape.",
      },
      item,
      index,
    );
  }

  function release() {
    setPicked(null);
    onPickedChange?.(null);
  }

  function cancel() {
    if (!active) return;
    const item = items[active.from];
    release();
    if (item === undefined) return;
    say({ id: "common.sortable.cancelled", defaultMessage: "{item}: cancelled, back at position {position} of {total}." }, item, active.from);
  }

  // Puts the picked row at `to`, a place in the order as shown.
  function place(to: number) {
    if (!active) return;
    const item = items[active.from];
    release();
    if (item === undefined) return;
    if (to !== active.from) onMove(active.from, to);
    say({ id: "common.sortable.dropped", defaultMessage: "{item} dropped at position {position} of {total}." }, item, to);
  }

  useEffect(() => {
    if (!picked) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") cancel();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  function onGripClick(event: ReactMouseEvent, key: string, index: number) {
    event.stopPropagation();
    if (swallowClick.current) {
      swallowClick.current = false;
      return;
    }
    if (active?.key === key) cancel();
    else if (active) place(index);
    else pickUp(index);
  }

  function onGripKeyDown(event: ReactKeyboardEvent, key: string, index: number) {
    const own = active?.key === key;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (own) place(active.to);
      else if (active) place(index);
      else pickUp(index);
    } else if (own && (event.key === "ArrowUp" || event.key === "ArrowDown")) {
      event.preventDefault();
      const to = Math.max(0, Math.min(items.length - 1, active.to + (event.key === "ArrowUp" ? -1 : 1)));
      if (to === active.to) return;
      setPicked({ key, to });
      say(MOVED, items[active.from] as T, to);
    }
  }

  function onGripPointerDown(event: ReactPointerEvent<HTMLElement>, key: string, index: number) {
    if (event.button !== 0) return;
    swallowClick.current = false;
    press.current = { key, index, y: event.clientY };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }

  function onGripPointerMove(event: ReactPointerEvent<HTMLElement>) {
    let current = drag;
    if (!current) {
      const pressed = press.current;
      if (!pressed || Math.abs(event.clientY - pressed.y) < DRAG_THRESHOLD_PX) return;
      if (active) release();
      current = { key: pressed.key, from: pressed.index, to: pressed.index };
    }
    // Compared with the neighbours' midpoints in the order as drawn, so rows of
    // different heights (a title on two lines) swap where the eye expects.
    const drawn = reordered(items, current.from, current.to);
    const middle = (index: number) => {
      const neighbour = drawn[index];
      const rect = neighbour === undefined ? undefined : rows.current.get(getKey(neighbour))?.getBoundingClientRect();
      return rect ? rect.top + rect.height / 2 : 0;
    };
    let to = current.to;
    while (to > 0 && event.clientY < middle(to - 1)) to -= 1;
    while (to < drawn.length - 1 && event.clientY > middle(to + 1)) to += 1;
    if (current !== drag || to !== current.to) setDrag({ ...current, to });
  }

  function onGripPointerUp() {
    press.current = null;
    if (!drag) return;
    swallowClick.current = true;
    const item = items[drag.from];
    if (drag.to !== drag.from && item !== undefined) {
      onMove(drag.from, drag.to);
      say(MOVED, item, drag.to);
    }
    setDrag(null);
  }

  function onGripPointerCancel() {
    press.current = null;
    setDrag(null);
  }

  return (
    <>
      <ol className={drag ? "select-none" : ""}>
        {shown.map((item, index) => {
          const key = getKey(item);
          const isPicked = active?.key === key;
          const washed = drag?.key === key || isPicked;
          // While a row is picked up, a tap anywhere on another row places it
          // there; the row's own controls (its remove button) keep their job.
          const placeHere = (event: ReactMouseEvent) => {
            if (!active || isPicked || (event.target as Element).closest("button, a, input, select, textarea")) return;
            place(index);
          };
          return (
            <li
              key={key}
              ref={(element) => {
                if (element) rows.current.set(key, element);
                else rows.current.delete(key);
              }}
              onClick={placeHere}
              className={`flex items-center gap-1 rounded-md pr-1 ${washed ? "bg-ink/10" : ""} ${
                active && !isPicked ? "cursor-pointer" : ""
              }`}
            >
              {/* touch-none keeps a finger on the grip from scrolling the page instead. */}
              <button
                type="button"
                aria-pressed={isPicked}
                aria-label={intl.formatMessage(
                  { id: "common.sortable.handle", defaultMessage: "Move {item}" },
                  { item: getLabel(item) },
                )}
                onClick={(event) => onGripClick(event, key, index)}
                onKeyDown={(event) => onGripKeyDown(event, key, index)}
                onPointerDown={(event) => onGripPointerDown(event, key, index)}
                onPointerMove={onGripPointerMove}
                onPointerUp={onGripPointerUp}
                onPointerCancel={onGripPointerCancel}
                className={buttonClasses({
                  variant: "icon",
                  size: "lg",
                  className: `shrink-0 touch-none select-none ${drag?.key === key ? "cursor-grabbing" : "cursor-grab"}`,
                })}
              >
                <GripVertical size={16} aria-hidden="true" />
              </button>
              <div className="min-w-0 flex-1">{renderItem(item, index)}</div>
            </li>
          );
        })}
      </ol>
      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>
    </>
  );
}

function reordered<T>(items: readonly T[], from: number, to: number): readonly T[] {
  if (from === to) return items;
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved as T);
  return next;
}
